import { PlusCircleOutlined, SyncOutlined } from '@ant-design/icons';
import { Button, Col, Input, Progress, Typography } from 'antd';
import { memo, useMemo, useState } from 'react';
import { ControllerProps, get, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useUploadContentsFile } from 'src/features/programsV2/hooks/useUploadFileContents';
import { ContentStepForm } from 'src/features/programsV2/schemas/contentStepSchema';
import { AllowedFileTypes } from 'src/features/programsV2/types/programModulesAndContents';
import { getFileName } from 'src/features/programsV2/utils/getFileName';
import { showErrorModal } from 'src/features/programsV2/utils/modalHelpers';
import { isFileSizeLess } from 'src/features/programsV2/utils/validateFileSize';

import { ProgramFormItem } from '../../ProgramFormItem';
import { TransparentButton } from '../../ui/TransparentButton';
import UploadDrawer, { DrawerFileProps } from '../../UploadDrawer';

const { Text } = Typography;

interface AddNewFileProps {
  contentType: string;
  selectedFileName: ControllerProps<ContentStepForm>['name'];
}

export const AddNewFile = memo<AddNewFileProps>(
  ({ contentType, selectedFileName }) => {
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const {
      control,
      watch,
      setValue,
      formState: { errors },
    } = useFormContext<ContentStepForm>();
    const params = useParams<{ id: string }>();
    const programId = Number(params.id);

    const { uploadFile, progress, isUploading } = useUploadContentsFile();

    const selectedFile = watch(selectedFileName);

    const isVideo = contentType === AllowedFileTypes.Video;
    const isAudio = contentType === AllowedFileTypes.Audio;

    const uploadMessage = useMemo(() => {
      const messages: Partial<Record<AllowedFileTypes, string>> = {
        [AllowedFileTypes.Video]: '.mp4, .mov ou .avi.',
        [AllowedFileTypes.Image]: '.png, .webp, .jpg ou .gif.',
        [AllowedFileTypes.Audio]: '.mp3, .wav ou .aac.',
        [AllowedFileTypes.PDF]: 'Arquivos .pdf.',
        [AllowedFileTypes.EBook]: '.epub ou .mobi, .doc, .docx, .xls ou .pdf.',
        [AllowedFileTypes.Document]: '.doc, .docx, .xls ou .txt., .pdf.',
        [AllowedFileTypes.CSV]:
          '.csv com os dados corretamente formatados e codificação UTF-8.',
      };

      return messages[contentType as AllowedFileTypes] ?? 'Arquivo válido.';
    }, [contentType]);

    const handleUpdateFile = () => {
      setIsDrawerOpen(true);
    };

    const handleSelectFile = async (file: DrawerFileProps) => {
      const formData = new FormData();
      formData.append('file', file.file);

      const isValidSize = isFileSizeLess(file.file);
      if (!isValidSize) {
        return;
      }

      try {
        const data = await uploadFile({ file: formData, programId });
        setValue(selectedFileName, {
          fileUrl: data?.imageUrl,
          fileName: getFileName(data?.imageUrl),
        });
      } catch (error) {
        showErrorModal({
          title: 'Tivemos um problema...',
          content: 'Não foi possível salvar este arquivo',
        });
      }
    };

    const error = get(errors, `${selectedFileName}`);

    return (
      <>
        {(selectedFile as any)?.fileUrl || isUploading ? (
          <Col span={!isVideo && !isAudio ? 15 : 6}>
            {isUploading ? (
              <Progress
                percent={progress}
                status={'active'}
                size='small'
                style={{
                  marginTop: 32,
                  padding: 0,
                  justifySelf: 'center',
                  width: '100%',
                }}
              />
            ) : (
              <ProgramFormItem
                control={control}
                name={`${selectedFileName}.fileName`}
                label='Arquivo'
                renderItem={({ field }) => (
                  <Input
                    {...field}
                    readOnly
                    style={{ cursor: 'initial' }}
                    suffix={
                      <TransparentButton
                        icon={<SyncOutlined />}
                        style={{
                          width: '14px',
                          height: '22px',
                        }}
                        onClick={handleUpdateFile}
                      />
                    }
                    disabled
                  />
                )}
              />
            )}
          </Col>
        ) : (
          <Col span={3}>
            <Button
              type='link'
              icon={<PlusCircleOutlined />}
              style={{
                marginTop: 26,
                padding: 0,
                justifySelf: 'center',
                width: '100%',
              }}
              onClick={() => setIsDrawerOpen(true)}
            >
              Selecionar arquivo
            </Button>
            {!!error && <Text type='danger'>{error.message}</Text>}
          </Col>
        )}
        {isDrawerOpen && (
          <UploadDrawer
            visible={isDrawerOpen}
            onChange={file => handleSelectFile?.(file)}
            onClose={() => setIsDrawerOpen(false)}
            contentType={contentType}
            uploadMessage={uploadMessage}
          />
        )}
      </>
    );
  }
);
